// Định nghĩa key lưu trữ chuẩn
export const STORAGE = {
  TOKEN: 'token-qlrv_hd',
  USER_INFO: 'user-info-qlrv_hd',
  REMEMBER_LOGIN: 'remember-login',
  DEV_MODE: 'dev-mode',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  KEY_MENU_ACTIVE: 'key-active',
  IS_SOUND_NOTIF: 'is-sound-notif',
  REMEMBER_HOME: 'remember-home',
  LAYOUT_SETTINGS: 'layout-settings-v2',
} as const

// Type của các key
export type StorageKey = (typeof STORAGE)[keyof typeof STORAGE]

// --------- LocalStorage -----------

export const getLocalStorage = <T = unknown>(key: StorageKey): T | null => {
  const data = localStorage.getItem(key)
  try {
    return data ? JSON.parse(data) : null
  } catch {
    return data as unknown as T
  }
}

export const setLocalStorage = (key: StorageKey, value: unknown): void => {
  const stringify = typeof value === 'string' ? value : JSON.stringify(value)
  localStorage.setItem(key, stringify)
}

export const deleteLocalStorage = (key: StorageKey): void => {
  localStorage.removeItem(key)
}

export const clearLocalStorage = (): void => {
  localStorage.clear()
}

// --------- SessionStorage -----------

export const getSessionStorage = <T = unknown>(key: StorageKey): T | null => {
  if (typeof window === 'undefined') return null
  const data = sessionStorage.getItem(key)
  try {
    return data ? JSON.parse(data) : null
  } catch {
    return data as unknown as T
  }
}

export const setSessionStorage = (key: StorageKey, value: unknown): void => {
  const stringify = typeof value === 'string' ? value : JSON.stringify(value)
  sessionStorage.setItem(key, stringify)
}

export const deleteSessionStorage = (key: StorageKey): void => {
  sessionStorage.removeItem(key)
}

export const clearSessionStorage = (): void => {
  sessionStorage.clear()
}

import { decryptToken, encryptToken } from '../crypto'

// ponytail: setAuthToken/setRefreshToken lưu bản đã encryptToken() — đọc lại phải
// decryptToken() tương ứng, không thì gửi thẳng ciphertext làm Bearer token lên BE.
// STORAGE.REMEMBER_HOME (ghi tại lúc login) là nguồn DUY NHẤT quyết định lưu
// localStorage (remember=true) hay sessionStorage (false). Khi flag thiếu (phiên
// cũ) → fallback giữ nguyên hành vi cũ, không đổi luồng hiện tại.
// Cờ nằm sessionStorage (riêng từng tab) để tab khác đăng nhập không đổi nơi đọc token
// của tab này. Cờ cũ trong localStorage bị bỏ qua → rơi vào nhánh legacy.
export const getRememberFlag = (): boolean | null => {
  const flag = getSessionStorage<boolean>(STORAGE.REMEMBER_HOME)
  return typeof flag === 'boolean' ? flag : null
}

// Xoá theo vùng của tab này: cờ=true chỉ local, cờ=false chỉ session, thiếu cờ (legacy) cả hai.
// Không đụng vùng còn lại để tab "không ghi nhớ" không làm mất khoá dùng chung của tab "ghi nhớ".
const deleteByFlag = (key: StorageKey): void => {
  const flag = getRememberFlag()
  if (flag === null || flag) deleteLocalStorage(key)
  if (flag === null || !flag) deleteSessionStorage(key)
}

// Chỉ khi chính tab này chuyển false → true mới dọn khoá auth ở sessionStorage (tab đó chuyển
// sang phiên dùng chung). true → false KHÔNG xoá localStorage: khoá local thuộc phiên dùng chung
// của các tab "ghi nhớ" khác.
export const setRememberFlag = (remember: boolean): void => {
  if (getRememberFlag() === false && remember) {
    for (const key of [STORAGE.TOKEN, STORAGE.REFRESH_TOKEN, STORAGE.USER_INFO] as const) {
      deleteSessionStorage(key)
    }
  }
  setSessionStorage(STORAGE.REMEMBER_HOME, remember)
}

// Cờ=true chỉ đọc localStorage, cờ=false chỉ đọc sessionStorage; chỉ khi thiếu cờ (phiên cũ)
// mới session ?? local như trước.
const readRaw = <T>(key: StorageKey): T | null => {
  const remember = getRememberFlag()
  if (remember === null) return getSessionStorage<T>(key) ?? getLocalStorage<T>(key)
  return remember ? getLocalStorage<T>(key) : getSessionStorage<T>(key)
}

const resolveRemember = (remember?: boolean): boolean => {
  const flag = getRememberFlag()
  if (flag !== null) return flag
  if (remember !== undefined) return remember
  // Legacy: flag thiếu → suy từ nơi access token đang nằm => khớp hành vi cũ
  return getLocalStorage<string>(STORAGE.TOKEN) !== null
}

const readRawToken = (key: StorageKey): string | null => readRaw<string>(key)

export const getAuthToken = (): string | null => {
  const raw = readRawToken(STORAGE.TOKEN)
  return raw ? decryptToken(raw) : null
}

export const getRefreshToken = (): string | null => {
  // Đọc theo cùng quy tắc access token (cờ remember; thiếu cờ → legacy) để phiên cũ
  // đang lưu localStorage vẫn đọc được.
  const raw = readRawToken(STORAGE.REFRESH_TOKEN)
  return raw ? decryptToken(raw) : null
}

export const setAuthToken = (token: string, remember?: boolean): void => {
  const encrypted = encryptToken(token)
  if (resolveRemember(remember)) {
    setLocalStorage(STORAGE.TOKEN, encrypted)
  } else {
    setSessionStorage(STORAGE.TOKEN, encrypted)
  }
}

export const setRefreshToken = (token: string, remember?: boolean): void => {
  const encrypted = encryptToken(token)
  // Giống access token: remember=true → localStorage, false → sessionStorage (riêng từng tab,
  // tránh hai tài khoản ở hai tab dùng chung refresh token).
  if (resolveRemember(remember)) {
    setLocalStorage(STORAGE.REFRESH_TOKEN, encrypted)
  } else {
    setSessionStorage(STORAGE.REFRESH_TOKEN, encrypted)
  }
}

export const deleteAuthToken = (): void => deleteByFlag(STORAGE.TOKEN)

export const deleteRefreshToken = (): void => deleteByFlag(STORAGE.REFRESH_TOKEN)

export const getUserInfo = <T = any>(): T | null => readRaw<T>(STORAGE.USER_INFO)

export const setUserInfo = (value: unknown, remember?: boolean): void => {
  if (resolveRemember(remember)) {
    setLocalStorage(STORAGE.USER_INFO, value)
  } else {
    setSessionStorage(STORAGE.USER_INFO, value)
  }
}

export const deleteUserInfo = (): void => deleteByFlag(STORAGE.USER_INFO)

// Đăng xuất: xoá khoá auth theo cờ, rồi xoá sạch sessionStorage. Cờ=false (tab không ghi nhớ)
// KHÔNG đụng localStorage; cờ=true hoặc thiếu giữ hành vi cũ (xoá sạch localStorage).
// Phải đọc cờ TRƯỚC khi clearSessionStorage vì cờ nằm ở sessionStorage.
export const clearAuthSession = (): void => {
  const flag = getRememberFlag()
  deleteAuthToken()
  deleteRefreshToken()
  deleteUserInfo()
  clearSessionStorage()
  if (flag !== false) clearLocalStorage()
}

export const storage = {
  getLocalStorage,
  setLocalStorage,
  deleteLocalStorage,
  clearLocalStorage,
  getSessionStorage,
  setSessionStorage,
  deleteSessionStorage,
  clearSessionStorage,
  getAuthToken,
  setAuthToken,
  deleteAuthToken,
  deleteRefreshToken,
  getUserInfo,
  setUserInfo,
  deleteUserInfo,
  clearAuthSession,
  getRememberFlag,
  setRememberFlag,
}
